import { userServiceApiSlice } from "state/services/userServiceApiSlice";

// Sync service calls (/api/sync/...) — same slice, base URL and bearer token as the other sync calls the app makes
// (see triggerOrgUserSync / getOrgUserSyncStatus). Every trigger is a POST with NO body: its arguments are query
// parameters. Only schedule create / update send a JSON body.

// Builds "?a=1&b=2" from the params that are actually set. Unset, null and blank values are left out (never the
// strings "undefined", "null" or ""), and booleans go as true / false.
export const buildQuery = (params) => {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === "string" && value.trim() === "") return;
    query.set(key, String(value).trim());
  });
  const text = query.toString();
  return text ? `?${text}` : "";
};

const scheduleUrl = (name) => `/sync/schedules/${encodeURIComponent(name)}`;
const LIST_TAG = { type: "SyncSchedule", id: "LIST" };

export const syncApi = userServiceApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // POST /sync/tasks/trigger/{path}?...  -> 202 { task_id, status: "queued", task, queue, ... }
    triggerSyncAction: builder.mutation({
      query: ({ path, params }) => ({ url: `/sync/tasks/trigger/${path}${buildQuery(params)}`, method: "POST" }),
    }),

    // POST /sync/tasks/trigger/org/{org_id}/verify-pending?queue=
    //   202 { task_id, status: "accepted", status_url }   or   200 { status: "already_running", message, ... }
    // Both are successful outcomes.
    triggerVerifyPendingOrg: builder.mutation({
      query: ({ orgId, queue }) => ({
        url: `/sync/tasks/trigger/org/${encodeURIComponent(orgId)}/verify-pending${buildQuery({ queue })}`,
        method: "POST",
      }),
    }),

    // GET /sync/tasks/status/{task_id}?resolve_unknown=true — raw Celery states (PENDING, STARTED, SUCCESS, FAILURE, ...).
    // With resolve_unknown a PENDING id is looked up in the retained history: a stored record comes back with its stored
    // state (status_source "database"); no record at all is a 404 (unknown or expired); a database failure is a 500.
    getSyncTaskStatus: builder.query({
      query: ({ taskId }) => ({ url: `/sync/tasks/status/${encodeURIComponent(taskId)}${buildQuery({ resolve_unknown: true })}` }),
      keepUnusedDataFor: 0,
    }),

    // ── Persistent run history (needs sync:task:result:read) ──
    // GET /sync/tasks/results -> { results, total_count, skip, limit }
    getSyncResults: builder.query({
      query: (params) => ({ url: `/sync/tasks/results${buildQuery(params)}` }),
      keepUnusedDataFor: 30,
    }),

    // GET /sync/tasks/results/{task_id} -> one stored record (not an envelope). 404 when there is none.
    getSyncResult: builder.query({
      query: ({ taskId }) => ({ url: `/sync/tasks/results/${encodeURIComponent(taskId)}` }),
      keepUnusedDataFor: 0,
    }),

    // GET /sync/tasks/results/metrics?lookback_hours= -> { range, by_task_name, totals, success_rate_percent }
    getSyncMetrics: builder.query({
      query: (params) => ({ url: `/sync/tasks/results/metrics${buildQuery(params)}` }),
      keepUnusedDataFor: 30,
    }),

    // GET /sync/tasks/results/latest-success -> { by_task_name: [{ task_name, last_success_at }] }
    getSyncLatestSuccess: builder.query({
      query: (params) => ({ url: `/sync/tasks/results/latest-success${buildQuery(params)}` }),
      keepUnusedDataFor: 30,
    }),

    // GET /sync/tasks/verify-pending/org/{org_id} — the organization verification's own lowercase states.
    getVerifyPendingOrgStatus: builder.query({
      query: ({ orgId }) => ({ url: `/sync/tasks/verify-pending/org/${encodeURIComponent(orgId)}` }),
      keepUnusedDataFor: 0,
    }),

    // GET /sync/schedules?enabled=true|false|all&skip=&limit= — a bare array sorted by name; the total before paging is in
    // the X-Total-Count header. Returned here as { items, total }.
    getSyncSchedules: builder.query({
      query: ({ enabled = "all", skip, limit } = {}) => ({ url: `/sync/schedules${buildQuery({ enabled, skip, limit })}` }),
      transformResponse: (data, meta) => {
        const items = Array.isArray(data) ? data : [];
        const header = Number(meta?.response?.headers?.get?.("X-Total-Count"));
        return { items, total: Number.isFinite(header) && header > 0 ? header : items.length };
      },
      providesTags: [LIST_TAG],
    }),

    // GET /sync/schedules/{name}[?include_last_task_run=true] — works for a disabled schedule too. 404 when it doesn't exist.
    // last_task_run is the latest execution of the schedule's TASK (it may have been started by hand or another schedule).
    getSyncSchedule: builder.query({
      query: ({ name, includeLastTaskRun }) => ({ url: `${scheduleUrl(name)}${buildQuery({ include_last_task_run: includeLastTaskRun ? true : undefined })}` }),
      providesTags: (result, error, { name }) => [{ type: "SyncSchedule", id: name }],
      keepUnusedDataFor: 0,
    }),

    // GET /sync/schedules/available -> { available_tasks: string[], count, message }
    getAvailableSyncTasks: builder.query({
      query: () => ({ url: "/sync/schedules/available" }),
    }),

    // POST /sync/schedules (201)
    createSyncSchedule: builder.mutation({
      query: (body) => ({ url: "/sync/schedules", method: "POST", body }),
      invalidatesTags: [LIST_TAG],
    }),

    // PUT /sync/schedules/{name} — the FULL schedule, not a patch.
    updateSyncSchedule: builder.mutation({
      query: ({ name, body }) => ({ url: scheduleUrl(name), method: "PUT", body }),
      invalidatesTags: (result, error, { name }) => [LIST_TAG, { type: "SyncSchedule", id: name }],
    }),

    deleteSyncSchedule: builder.mutation({
      query: ({ name }) => ({ url: scheduleUrl(name), method: "DELETE" }),
      invalidatesTags: (result, error, { name }) => [LIST_TAG, { type: "SyncSchedule", id: name }],
    }),

    enableSyncSchedule: builder.mutation({
      query: ({ name }) => ({ url: `${scheduleUrl(name)}/enable`, method: "POST" }),
      invalidatesTags: (result, error, { name }) => [LIST_TAG, { type: "SyncSchedule", id: name }],
    }),

    disableSyncSchedule: builder.mutation({
      query: ({ name }) => ({ url: `${scheduleUrl(name)}/disable`, method: "POST" }),
      invalidatesTags: (result, error, { name }) => [LIST_TAG, { type: "SyncSchedule", id: name }],
    }),
  }),
});

export const {
  useTriggerSyncActionMutation,
  useTriggerVerifyPendingOrgMutation,
  useGetSyncTaskStatusQuery,
  useLazyGetSyncTaskStatusQuery,
  useLazyGetVerifyPendingOrgStatusQuery,
  useGetSyncResultsQuery,
  useGetSyncResultQuery,
  useGetSyncMetricsQuery,
  useGetSyncLatestSuccessQuery,
  useGetSyncSchedulesQuery,
  useGetSyncScheduleQuery,
  useLazyGetSyncScheduleQuery,
  useGetAvailableSyncTasksQuery,
  useCreateSyncScheduleMutation,
  useUpdateSyncScheduleMutation,
  useDeleteSyncScheduleMutation,
  useEnableSyncScheduleMutation,
  useDisableSyncScheduleMutation,
} = syncApi;
